import { HttpStatusCodes } from '@service-initiation/shared-clients-http';
import { logger } from '@service-initiation/shared-clients-osp-logger';
import { queryClient } from '@service-initiation/shared-utils-query-client';
import { convertTimeSlotToTimestamp } from '@service-initiation/tech-care-utils-date-time';

import { techCareServicesClient } from './clients/techCareServicesClient';
import { type AppointmentIntentId } from './types/AppointmentIntentId';

export const CREATE_APPOINTMENT_INTENT_CACHE_KEY = 'createAppointmentIntent';

export type CreateAppointmentIntentProps = {
  appointmentDate: string;
  appointmentStartTime: string;
  storeId: number;
  storeTimeZone: string;
};

export const generateCreateAppointmentIntentCacheKey = (
  request: CreateAppointmentIntentProps
) => [
  CREATE_APPOINTMENT_INTENT_CACHE_KEY,
  {
    appointmentDate: request.appointmentDate,
    appointmentStartTime: request.appointmentStartTime,
    storeId: request.storeId,
    storeTimeZone: request.storeTimeZone,
  },
];

export const createAppointmentIntent = async (
  request: CreateAppointmentIntentProps
): Promise<AppointmentIntentId> => {
  try {
    const appointmentIntentId = await queryClient.fetchQuery({
      queryFn: async () => {
        const timeStamp = convertTimeSlotToTimestamp(
          request.appointmentDate,
          request.appointmentStartTime,
          request.storeTimeZone
        );
        const response = await techCareServicesClient.createAppointmentIntent({
          store_id: request.storeId,
          time: timeStamp,
        });

        if (response.status !== HttpStatusCodes.OK) {
          throw new Error('Received invalid response status from server.');
        }

        if (response.body?.data.error || !response.body.data.intent_id) {
          throw new Error('Failed to create appointment intent.');
        }

        return response.body.data.intent_id;
      },
      queryKey: generateCreateAppointmentIntentCacheKey(request),
      retry: 2,
      staleTime: Infinity,
    });

    return appointmentIntentId;
  } catch (e) {
    const err = e as Error;
    err.message = 'Error occurred while trying to create appointment intent.';
    logger.error(err, {
      data: request,
    });
    throw err;
  }
};
